import React from 'react';
import { CheckCircle, Award, Target, Users2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent, whyChooseUs, teamMembers } from '../data/mock';
import * as Icons from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center bg-gradient-to-r from-slate-900 to-slate-700">
        <div className="container mx-auto px-4 z-10">
          <Badge className="mb-4 bg-blue-600 text-white border-0">About Us</Badge>
          <h1 className="text-5xl font-bold text-white mb-6">
            Building Trust Through Excellence
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl">
            Over a decade of experience in delivering superior medical infrastructure and technical solutions.
          </p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mb-6">
                  <Target size={28} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
                <p className="text-slate-600 leading-relaxed">{aboutContent.mission}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mb-6">
                  <Award size={28} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h2>
                <p className="text-slate-600 leading-relaxed">{aboutContent.vision}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Who We Are</Badge>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Your Trusted Partner in Medical Infrastructure
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {aboutContent.philosophy}
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                S2U Enterprises is a group of professionals with extensive experience in delivering comprehensive solutions for medical infrastructure, electrical systems, HVAC, and turnkey projects. We serve small, medium, and large enterprises with equal dedication and professionalism.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-1">10+</div>
                  <div className="text-slate-600 text-sm">Years Experience</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-1">150+</div>
                  <div className="text-slate-600 text-sm">Projects Completed</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-1">100+</div>
                  <div className="text-slate-600 text-sm">Happy Clients</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-1">24/7</div>
                  <div className="text-slate-600 text-sm">Service Available</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg"
                alt="Team Meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Values</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Drives Us</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our core values shape every decision we make and every project we deliver.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutContent.values.map((value, index) => (
              <Card key={index} className="border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{value}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Committed to maintaining the highest standards in all our operations and relationships.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Why Choose S2U</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">We Promise To</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our commitment to excellence goes beyond promises—it's embedded in everything we do.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = Icons[item.icon];
              return (
                <Card key={index} className="border-slate-200 hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Team</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Meet Our Leadership</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Experienced professionals dedicated to delivering excellence in every project.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card key={index} className="text-center border-slate-200 hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <CardContent className="p-6">
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 overflow-hidden border-4 border-blue-100 group-hover:border-blue-600 transition-colors">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-medium">{member.designation}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-slate-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Our Commitment</h2>
              <p className="text-xl text-blue-100">
                We believe in maintaining assets with consistent performance and providing excellent after-sales service support.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2">Quality Assurance</h3>
                <p className="text-blue-100">Maintaining quality standards at each step of our business process</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Users2 size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2">Customer Focus</h3>
                <p className="text-blue-100">Building long-term relationships through exceptional service</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Award size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2">Ethical Practice</h3>
                <p className="text-blue-100">Operating on principles of integrity and transparency</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
